import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';

import { createApiClient, currentTenantId } from '../api.js';

/**
 * Setup checklist card (card spotlight-090).
 *
 * Drop-in panel that mounts on the Home + Project Overview surfaces
 * and renders the auto-introspected setup checklist for the current
 * tenant or project.  Each row is a `{id, label, done, action_url}`
 * tuple coming straight from `GET /v1/admin/setup/{tenant,project}`
 * — the dashboard never reproduces the "is this done?" probe logic
 * client-side.
 *
 * Two surfaces, same component:
 *
 *   <SetupChecklistCard scope="tenant" />
 *   <SetupChecklistCard scope="project" projectId="proj_…" />
 *
 * Behaviour:
 *
 *   - First load: render the full list with each item's "Set up →"
 *     link.  Done items get a check + crossed-out style so the
 *     operator's eye snaps to the unfinished rows.
 *   - All items done: collapse to a single `✅ Setup complete` pill.
 *   - Dismiss: the card hides via sessionStorage keyed by
 *     (operator, tenant) or (operator, project) so a refresh keeps
 *     the dismiss, but a new tab brings it back if anything's
 *     regressed.  A small "Show setup" affordance in the header
 *     lets the user pop it back.
 *
 *   - Failure: a 4xx/5xx renders nothing.  The checklist is a
 *     nudge, not a blocker — a 500 because the backend just
 *     deployed shouldn't paper over the actual Home content.
 */
export function SetupChecklistCard({ scope, projectId = null }) {
  const { user } = useUser();
  const { getToken } = useAuth();
  const api = useMemo(() => createApiClient(getToken), [getToken]);

  const operatorId = user?.id || 'anon';
  const tenantId = currentTenantId();

  // sessionStorage dismiss is scoped per (operator, tenant) for the
  // tenant card and per (operator, project) for the project card.
  // Using sessionStorage (not localStorage) so a new browser tab
  // brings the checklist back if anything's regressed — a
  // "dismissed forever" preference would mask new wiring drift the
  // operator should know about.
  const dismissKey = useMemo(() => {
    if (scope === 'tenant') {
      return `spotlight.setup.dismiss.tenant.${operatorId}.${tenantId}`;
    }
    return `spotlight.setup.dismiss.project.${operatorId}.${projectId || ''}`;
  }, [scope, operatorId, tenantId, projectId]);

  const [dismissed, setDismissed] = useState(() => readDismiss(dismissKey));
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);

  // Re-read dismiss whenever the key flips (tenant or project switch)
  // so the card re-appears on a new context even if the previous
  // one was dismissed.
  useEffect(() => {
    setDismissed(readDismiss(dismissKey));
  }, [dismissKey]);

  useEffect(() => {
    if (scope === 'project' && !projectId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const path =
          scope === 'tenant'
            ? '/v1/admin/setup/tenant'
            : `/v1/admin/setup/project?project_id=${encodeURIComponent(projectId)}`;
        const resp = await api(path);
        if (!cancelled) setPayload(resp || null);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load setup checklist.');
      }
    })();
    return () => { cancelled = true; };
  }, [api, scope, projectId]);

  if (error) return null;
  if (!payload) return null;

  const items = Array.isArray(payload.items) ? payload.items : [];
  if (items.length === 0) return null;

  const doneCount = items.filter((i) => i.done).length;
  const complete = !!payload.complete;

  if (dismissed) {
    // Allow the operator to pop the card back via a small affordance
    // — only when the install is not yet complete (a complete
    // install has nothing to nudge about).
    if (complete) return null;
    return (
      <div className="setup-pill setup-pill--reopen">
        <span>Setup in progress · {doneCount}/{items.length}</span>
        <button
          type="button"
          className="setup-pill__btn"
          onClick={() => {
            try { sessionStorage.removeItem(dismissKey); } catch { /* no-op */ }
            setDismissed(false);
          }}
        >
          Show setup
        </button>
        <style>{SETUP_CSS}</style>
      </div>
    );
  }

  if (complete) {
    return (
      <div className="setup-pill setup-pill--complete">
        <span aria-hidden="true">✅</span>
        <span>Setup complete</span>
        <button
          type="button"
          className="setup-pill__btn"
          onClick={() => persistDismiss(dismissKey, setDismissed)}
          title="Hide this pill"
        >
          Dismiss
        </button>
        <style>{SETUP_CSS}</style>
      </div>
    );
  }

  return (
    <section className="setup-card" aria-label="Setup checklist">
      <header className="setup-card__head">
        <div>
          <div className="setup-card__title">
            {scope === 'tenant' ? 'Tenant setup' : 'Project setup'}
          </div>
          <div className="setup-card__sub">
            {doneCount} of {items.length} complete
          </div>
        </div>
        <button
          type="button"
          className="setup-card__dismiss"
          onClick={() => persistDismiss(dismissKey, setDismissed)}
          title="Hide this checklist"
        >
          Dismiss
        </button>
      </header>
      <ul className="setup-list">
        {items.map((item) => (
          <li
            key={item.id}
            className={`setup-row${item.done ? ' setup-row--done' : ''}`}
          >
            <span className="setup-row__glyph" aria-hidden="true">
              {item.done ? '✓' : '○'}
            </span>
            <span className="setup-row__label">{item.label}</span>
            {!item.done && item.action_url && (
              <Link to={item.action_url} className="setup-row__cta">
                Set up →
              </Link>
            )}
          </li>
        ))}
      </ul>
      <style>{SETUP_CSS}</style>
    </section>
  );
}

function readDismiss(key) {
  if (typeof window === 'undefined') return false;
  try { return window.sessionStorage.getItem(key) === '1'; }
  catch { return false; }
}

function persistDismiss(key, setDismissed) {
  try { sessionStorage.setItem(key, '1'); } catch { /* private mode */ }
  setDismissed(true);
}

const SETUP_CSS = `
  .setup-card {
    background: var(--surface-0, #fff);
    border: 1px solid var(--border-1, #e5e7eb);
    border-left: 3px solid var(--brand-600, #4f46e5);
    border-radius: 12px;
    padding: 14px 18px;
    margin-bottom: 16px;
  }
  .setup-card__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
  }
  .setup-card__title {
    font-size: 13.5px;
    font-weight: 700;
    color: var(--text-1, #0f172a);
  }
  .setup-card__sub {
    font-size: 12px;
    color: var(--text-3, #64748b);
    margin-top: 2px;
  }
  .setup-card__dismiss {
    background: transparent;
    border: 1px solid var(--border-1, #e5e7eb);
    border-radius: 8px;
    padding: 4px 10px;
    font-size: 11.5px;
    font-weight: 600;
    color: var(--text-3, #64748b);
    cursor: pointer;
  }
  .setup-card__dismiss:hover {
    color: var(--text-1, #0f172a);
    background: var(--surface-1, #f8fafc);
  }

  .setup-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .setup-row {
    display: grid;
    grid-template-columns: 22px 1fr auto;
    align-items: center;
    gap: 10px;
    padding: 6px 8px;
    border-radius: 8px;
    font-size: 13px;
    color: var(--text-2, #334155);
  }
  .setup-row:hover { background: var(--surface-1, #f8fafc); }
  .setup-row--done .setup-row__label {
    color: var(--text-3, #64748b);
    text-decoration: line-through;
  }
  .setup-row__glyph {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--surface-1, #f1f5f9);
    color: var(--text-3, #64748b);
    font-size: 11px;
    font-weight: 700;
  }
  .setup-row--done .setup-row__glyph {
    background: var(--brand-50, #eef2ff);
    color: var(--brand-700, #4338ca);
  }
  .setup-row__cta {
    font-size: 12px;
    font-weight: 600;
    color: var(--brand-700, #4338ca);
    text-decoration: none;
    padding: 2px 8px;
    border-radius: 6px;
    transition: background 120ms ease;
  }
  .setup-row__cta:hover {
    background: var(--brand-50, #eef2ff);
    text-decoration: none;
  }

  .setup-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    border-radius: 9999px;
    background: var(--surface-0, #fff);
    border: 1px solid var(--border-1, #e5e7eb);
    font-size: 12.5px;
    font-weight: 600;
    color: var(--text-2, #334155);
    margin-bottom: 16px;
  }
  .setup-pill--complete {
    border-color: var(--brand-200, #c7d2fe);
    background: var(--brand-50, #eef2ff);
    color: var(--brand-700, #4338ca);
  }
  .setup-pill__btn {
    background: transparent;
    border: 0;
    color: inherit;
    font: inherit;
    cursor: pointer;
    text-decoration: underline;
    padding: 0;
  }
`;
